/* ============================================================
   Henry Duvall: Styled For You, shop the look

   Desktop: every piece in the look is listed, nothing hidden.
   Under 768px the list becomes a swipeable row of cards.
   In both, the numbered dots on the photo and the pieces stay
   in sync, and the bag button counts down what is still
   unchosen instead of failing when something is missing.

   Adds go through Symmetry's own cart pipeline, so the header
   count, the cart drawer and the added notification behave as
   they do everywhere else on the site.
   ============================================================ */
(function () {
  'use strict';

  var SLIDER = '(max-width: 767px)';

  function parseJSON(el, fallback) {
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withDecimals = parts.join('.');
    var noDecimals = parts[0];
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
  }

  class ShopTheLookElement extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.addUrl = this.dataset.cartAddUrl || '/cart/add.js';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.strings = {
        added: this.dataset.stringAdded || 'Added to your bag',
        choose: this.dataset.stringChoose || 'Choose a size for this piece',
        viewCart: this.dataset.stringViewCart || 'View bag',
        error: this.dataset.stringError || 'Sorry, that could not be added.',
        soldOut: this.dataset.stringSoldOut || 'Sold out',
        unavailable: this.dataset.stringUnavailable || 'That combination is sold out',
        statusNone: this.dataset.stringStatusNone || 'No sizes chosen yet',
        statusProgress: this.dataset.stringStatusProgress || '[done] of [total] pieces ready',
        statusTotal: this.dataset.stringStatusTotal || '[amount] ready to add',
        editorHint: this.dataset.editorHint || 'Copied.'
      };

      this.looks = Array.prototype.slice.call(this.querySelectorAll('.sfy-look'));
      this.toast = this.querySelector('.sfy-toast');
      this.emptyState = this.querySelector('.sfy-empty');

      this.looks.forEach(this.setupLook.bind(this));
      this.setupFilters();
      this.setupEditorHelpers();
    }

    /* ---------------- one look ---------------- */
    setupLook(look) {
      var self = this;
      var rows = Array.prototype.slice.call(look.querySelectorAll('.sfy-item[data-index]'));
      var pins = Array.prototype.slice.call(look.querySelectorAll('.sfy-pin'));
      var strip = look.querySelector('.sfy-items');

      look.sfyRows = rows;
      look.sfyPins = pins;
      look.sfyStrip = strip;

      pins.forEach(function (pin) {
        var row = rows[parseInt(pin.dataset.piece, 10) - 1];
        if (!row) return;

        pin.addEventListener('mouseenter', function () { self.setActive(look, row); });
        pin.addEventListener('focus', function () { self.setActive(look, row); });
        pin.addEventListener('click', function () { self.goToPiece(look, row); });

        row.addEventListener('mouseenter', function () { self.setActive(look, row); });
        row.addEventListener('focusin', function () { self.setActive(look, row); });
      });

      look.addEventListener('mouseleave', function () { self.setActive(look, null); });

      rows.forEach(function (row) {
        row.data = parseJSON(row.querySelector('.sfy-item__data'), { variants: [] });

        row.querySelectorAll('.sfy-item__option').forEach(function (select) {
          Array.prototype.forEach.call(select.options, function (opt) {
            opt.dataset.label = opt.textContent.trim();
          });
          select.addEventListener('change', function () {
            self.resolveVariant(row);
            self.updateLookState(look);
          });
        });

        var addBtn = row.querySelector('.sfy-item__add');
        if (addBtn) {
          addBtn.addEventListener('click', function () {
            var variantId = self.variantFor(row);
            if (!variantId) {
              self.flagRow(row, self.strings.choose);
              self.goToPiece(look, row);
              return;
            }
            self.addToCart([{ id: variantId, quantity: 1 }], addBtn);
          });
        }
      });

      var allBtn = look.querySelector('[data-add-all]');
      if (allBtn) {
        allBtn.addEventListener('click', function () {
          var items = [];
          var missing = [];
          rows.forEach(function (row) {
            if (row.dataset.soldOut === 'true') return;
            var variantId = self.variantFor(row);
            if (!variantId) {
              missing.push(row);
              self.flagRow(row, self.strings.choose);
            } else {
              items.push({ id: variantId, quantity: 1 });
            }
          });
          if (missing.length) {
            self.goToPiece(look, missing[0]);
            return;
          }
          if (!items.length) return;
          self.addToCart(items, allBtn);
        });
      }

      /* swiping the row of cards moves the highlight on the photo */
      if (strip) {
        var timer;
        strip.addEventListener('scroll', function () {
          if (!window.matchMedia(SLIDER).matches) return;
          window.clearTimeout(timer);
          timer = window.setTimeout(function () {
            var middle = strip.scrollLeft + strip.clientWidth / 2;
            var nearest = null;
            var best = Infinity;
            rows.forEach(function (row) {
              var centre = row.offsetLeft - strip.offsetLeft + row.offsetWidth / 2;
              var distance = Math.abs(centre - middle);
              if (distance < best) { best = distance; nearest = row; }
            });
            self.setActive(look, nearest);
          }, 90);
        });
      }

      this.updateLookState(look);
    }

    setActive(look, row) {
      (look.sfyRows || []).forEach(function (r, i) {
        var on = r === row;
        r.classList.toggle('is-active', on);
        if (look.sfyPins[i]) look.sfyPins[i].classList.toggle('is-active', on);
      });
    }

    /* On desktop every piece is already on screen, so a dot only has to
       point at one. In the swipeable row it has to travel there first. */
    goToPiece(look, row) {
      this.setActive(look, row);
      var select = row.querySelector('select.sfy-item__option');
      var strip = look.sfyStrip;

      if (window.matchMedia(SLIDER).matches && strip) {
        var behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        strip.scrollTo({ left: row.offsetLeft - strip.offsetLeft, behavior: behavior });
        if (select) {
          window.setTimeout(function () { select.focus({ preventScroll: true }); }, behavior === 'smooth' ? 320 : 0);
        }
        return;
      }

      if (select) select.focus({ preventScroll: true });
    }

    /* Used by links from product pages: light up one piece without
       stealing focus. In the swipeable row, bring that card into view. */
    highlightPiece(look, row) {
      if (!row) return;
      this.setActive(look, row);
      row.classList.add('is-linked');
      var strip = look.sfyStrip;
      if (window.matchMedia(SLIDER).matches && strip) {
        strip.scrollTo({ left: row.offsetLeft - strip.offsetLeft, behavior: 'auto' });
      }
    }

    variantFor(row) {
      var field = row.querySelector('.sfy-item__variant');
      return field && field.value ? field.value : null;
    }

    /* Work out which variant the dropdowns describe, then keep the
       dropdowns honest about what is still in stock. */
    resolveVariant(row) {
      var self = this;
      var data = row.data || { variants: [] };
      var selects = Array.prototype.slice.call(row.querySelectorAll('.sfy-item__option'));
      var chosen = selects.map(function (s) { return s.value || null; });
      var field = row.querySelector('.sfy-item__variant');
      var msg = row.querySelector('.sfy-item__msg');

      var match = null;
      if (chosen.every(function (v) { return v; })) {
        match = data.variants.filter(function (variant) {
          return chosen.every(function (value, i) { return variant.options[i] === value; });
        })[0] || null;
      }

      row.classList.remove('has-error');

      if (match && match.available) {
        field.value = match.id;
        row.classList.add('is-chosen');
        if (msg) { msg.textContent = ''; msg.classList.remove('is-visible'); }
      } else {
        field.value = '';
        row.classList.remove('is-chosen');
        if (match && !match.available && msg) {
          msg.textContent = this.strings.unavailable;
          msg.classList.add('is-visible');
          row.classList.add('has-error');
        } else if (msg) {
          msg.textContent = '';
          msg.classList.remove('is-visible');
        }
      }

      selects.forEach(function (select, index) {
        Array.prototype.forEach.call(select.options, function (opt) {
          if (!opt.value) return;
          var possible = data.variants.some(function (variant) {
            if (!variant.available) return false;
            if (variant.options[index] !== opt.value) return false;
            return chosen.every(function (value, i) {
              return i === index || !value || variant.options[i] === value;
            });
          });
          opt.disabled = !possible;
          opt.textContent = possible ? opt.dataset.label : opt.dataset.label + ' · ' + self.strings.soldOut;
        });
      });
    }

    flagRow(row, message) {
      row.classList.add('has-error');
      var msg = row.querySelector('.sfy-item__msg');
      if (msg) {
        msg.textContent = message;
        msg.classList.add('is-visible');
      }
    }

    /* The bar under each look says what is still missing, so nobody has
       to press a button to find out. */
    updateLookState(look) {
      var btn = look.querySelector('[data-add-all]');
      var rows = look.sfyRows || [];
      var pips = Array.prototype.slice.call(look.querySelectorAll('.sfy-progress__pip'));
      var statusLeft = look.querySelector('[data-status-left]');
      var statusRight = look.querySelector('[data-status-right]');

      var addable = rows.filter(function (row) { return row.dataset.soldOut !== 'true'; });
      var ready = 0;
      var total = 0;

      rows.forEach(function (row, i) {
        var chosen = !!row.querySelector('.sfy-item__variant').value;
        if (pips[i]) pips[i].classList.toggle('is-done', chosen);
        if (look.sfyPins[i]) look.sfyPins[i].classList.toggle('is-done', chosen);
        if (chosen) {
          ready++;
          total += parseInt(row.dataset.price || '0', 10);
        }
      });

      if (statusLeft) {
        statusLeft.textContent = ready === 0
          ? this.strings.statusNone
          : this.strings.statusProgress
              .replace('[done]', ready)
              .replace('[total]', addable.length);
      }
      if (statusRight) {
        statusRight.textContent = this.strings.statusTotal
          .replace('[amount]', formatMoney(total, this.moneyFormat));
      }

      if (!btn) return;

      if (!addable.length) {
        btn.disabled = true;
        btn.classList.remove('is-incomplete');
        btn.textContent = this.strings.soldOut;
        return;
      }

      var missing = addable.length - ready;
      btn.classList.toggle('is-incomplete', missing > 0);
      if (missing === 0) {
        btn.textContent = btn.dataset.labelReady;
      } else if (missing === 1) {
        btn.textContent = (btn.dataset.labelNeedsOne || 'Choose 1 more size').replace('[n]', '1');
      } else {
        btn.textContent = (btn.dataset.labelNeedsMany || 'Choose [n] sizes').replace('[n]', missing);
      }
    }

    /* ---------------- filters ---------------- */
    setupFilters() {
      var self = this;
      var buttons = Array.prototype.slice.call(this.querySelectorAll('.sfy-filter'));
      if (!buttons.length) return;
      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          buttons.forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
          var filter = btn.dataset.filter;
          var shown = 0;
          self.looks.forEach(function (look) {
            var cats = (look.dataset.cat || '').split(' ').filter(Boolean);
            var match = filter === 'all' || cats.indexOf(filter) !== -1;
            look.hidden = !match;
            if (match) shown++;
          });
          if (self.emptyState) self.emptyState.classList.toggle('is-visible', shown === 0);
        });
      });
    }

    /* ---------------- cart ---------------- */
    addToCart(items, button, onDone) {
      var self = this;
      var original = button.textContent;
      button.disabled = true;

      var sectionEls = ['page-header', 'cart-drawer']
        .map(function (sel) { return document.querySelector(sel); })
        .filter(function (el) { return el && el.dataset.sectionId; });

      var body = new FormData();
      items.forEach(function (item, i) {
        body.append('items[' + i + '][id]', item.id);
        body.append('items[' + i + '][quantity]', item.quantity || 1);
      });
      if (sectionEls.length) {
        body.append('sections_url', window.location.pathname);
        body.append('sections', sectionEls.map(function (el) { return el.dataset.sectionId; }).join(','));
      }

      var url = (window.theme && theme.routes && theme.routes.cartAdd) || this.addUrl;

      fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok || data.status) {
              throw new Error(data.description || data.message || self.strings.error);
            }
            return data;
          });
        })
        .then(function (data) {
          button.disabled = false;
          self.markButtonDone(button, original, items.length);
          self.handleCartResponse(data, sectionEls, items.length);
          if (typeof onDone === 'function') onDone();
        })
        .catch(function (err) {
          button.disabled = false;
          button.textContent = original;
          self.notify(err.message || self.strings.error, true);
        });
    }

    markButtonDone(button, original, count) {
      button.classList.add('is-done');
      button.textContent = count > 1 ? 'Added ' + count + ' pieces ✓' : 'Added ✓';
      setTimeout(function () {
        button.classList.remove('is-done');
        button.textContent = original;
      }, 1600);
    }

    handleCartResponse(data, sectionEls, count) {
      var settings = (window.theme && theme.settings) || {};

      if (data.sections) {
        sectionEls.forEach(function (el) {
          var html = data.sections[el.dataset.sectionId];
          if (html && typeof el.updateFromCartChange === 'function') el.updateFromCartChange(html);
        });
      }

      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: { variantId: data.variant_id }
      }));

      if (settings.afterAddToCart === 'page' && window.theme.routes) {
        window.location.href = theme.routes.cart;
        return;
      }

      var drawer = document.querySelector('.js-cart-drawer');
      if (settings.afterAddToCart === 'drawer' && drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }

      if (settings.afterAddToCart === 'notification' && this.showThemeNotification(data, count)) return;

      this.notify(this.strings.added);
    }

    showThemeNotification(data, count) {
      var template = document.getElementById('AddedNotification');
      if (!template || !template.content || !template.content.firstElementChild) return false;

      var notification = template.content.firstElementChild.cloneNode(true);
      var title;
      if (count > 1) {
        title = count + ' pieces';
      } else {
        title = data.product_title || (data.items && data.items.length ? data.items[0].product_title : '');
      }
      notification.dataset.productTitle = title || this.strings.added;
      var container = document.querySelector('.pageheader--sticky') || document.body;
      container.appendChild(notification);
      return true;
    }

    notify(message, isError) {
      var self = this;
      if (!this.toast) return;
      this.toast.innerHTML = '';
      var span = document.createElement('span');
      span.textContent = message;
      this.toast.appendChild(span);
      if (!isError) {
        var link = document.createElement('a');
        link.href = this.cartUrl;
        link.textContent = this.strings.viewCart;
        this.toast.appendChild(link);
      }
      this.toast.classList.add('is-visible');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(function () {
        self.toast.classList.remove('is-visible');
      }, 4000);
    }

    /* ---------------- theme editor helpers ---------------- */
    setupEditorHelpers() {
      var self = this;
      if (!window.Shopify || !Shopify.designMode) return;
      this.classList.add('sfy-editing');

      this.querySelectorAll('.sfy-look__photo').forEach(function (photo) {
        photo.addEventListener('click', function (evt) {
          if (evt.target.closest('.sfy-pin')) return;
          var rect = photo.getBoundingClientRect();
          var x = Math.round(((evt.clientX - rect.left) / rect.width) * 100);
          var y = Math.round(((evt.clientY - rect.top) / rect.height) * 100);
          self.showCoords(photo, evt.clientX - rect.left, evt.clientY - rect.top, x, y);
        });
      });

      document.addEventListener('shopify:block:select', function (evt) {
        var pin = self.querySelector('.sfy-pin[data-block-id="' + evt.detail.blockId + '"]');
        if (!pin) return;
        self.querySelectorAll('.sfy-pin').forEach(function (p) { p.classList.remove('is-editing'); });
        pin.classList.add('is-editing');
        pin.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      document.addEventListener('shopify:block:deselect', function () {
        self.querySelectorAll('.sfy-pin').forEach(function (p) { p.classList.remove('is-editing'); });
      });
    }

    showCoords(photo, left, top, x, y) {
      var badge = photo.querySelector('.sfy-coords');
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'sfy-coords';
        photo.appendChild(badge);
      }
      badge.innerHTML = '<strong>across ' + x + ' · down ' + y + '</strong><span>' + this.strings.editorHint + '</span>';
      badge.style.left = left + 'px';
      badge.style.top = top + 'px';
      badge.classList.add('is-visible');

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(x + ', ' + y).catch(function () {});
      }
      clearTimeout(this.coordsTimer);
      this.coordsTimer = setTimeout(function () { badge.classList.remove('is-visible'); }, 4000);
    }
  }

  if (!customElements.get('sfy-shop-the-look')) {
    customElements.define('sfy-shop-the-look', ShopTheLookElement);
  }

  /* ============================================================
     Page controller: filter chips + "Show more"
     Lives in the SFY: Looks header section and works on every
     SFY: Look section on the page.
     ============================================================ */
  function lookWrapper(look) {
    return look.closest('.shopify-section') || look.closest('sfy-shop-the-look') || look;
  }

  function allLooks() {
    return Array.prototype.slice.call(document.querySelectorAll('.sfy-stl--single .sfy-look[data-look-id]'));
  }

  class LooksController extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      var self = this;
      this.initial = parseInt(this.dataset.initial || '0', 10) || 0;
      this.moreLabel = this.dataset.moreLabel || 'Show more looks ([n])';
      this.filter = 'all';
      this.expanded = false;
      this.emptyState = this.querySelector('.sfy-empty');
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('.sfy-filter'));

      this.buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.setFilter(btn.dataset.filter);
        });
      });

      /* the "Show more" button sits after the last visible look */
      this.more = document.createElement('div');
      this.more.className = 'sfy sfy-more';
      this.more.innerHTML = '<div class="sfy-container"><button type="button" class="btn btn--secondary sfy-more__btn"></button></div>';
      this.moreBtn = this.more.querySelector('button');
      this.moreBtn.addEventListener('click', function () {
        self.expanded = true;
        self.apply();
      });

      /* the theme editor is easier to use with everything showing */
      if (window.Shopify && Shopify.designMode) this.expanded = true;

      this.apply();
    }

    setFilter(filter) {
      this.filter = filter || 'all';
      this.buttons.forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.dataset.filter === filter));
      });
      this.apply();
    }

    matches(look) {
      if (this.filter === 'all') return true;
      var cats = (look.dataset.cat || '').split(' ').filter(Boolean);
      return cats.indexOf(this.filter) !== -1;
    }

    apply() {
      var self = this;
      var looks = allLooks();
      var shown = 0;
      var matched = 0;
      var lastShown = null;

      looks.forEach(function (look) {
        var wrap = lookWrapper(look);
        var visible = false;
        if (self.matches(look)) {
          matched++;
          if (self.expanded || !self.initial || matched <= self.initial) visible = true;
        }
        wrap.style.display = visible ? '' : 'none';
        look.hidden = false;
        if (visible) { shown++; lastShown = wrap; }
      });

      if (this.emptyState) this.emptyState.classList.toggle('is-visible', shown === 0);

      var remaining = matched - shown;
      if (remaining > 0 && lastShown) {
        this.moreBtn.textContent = this.moreLabel.replace('[n]', remaining);
        lastShown.parentNode.insertBefore(this.more, lastShown.nextSibling);
        this.more.hidden = false;
      } else {
        this.more.hidden = true;
      }
    }

    /* make sure a look someone linked to is on screen */
    reveal(look) {
      if (!this.matches(look)) this.setFilter('all');
      this.expanded = true;
      this.apply();
    }
  }

  if (!customElements.get('sfy-looks-controller')) {
    customElements.define('sfy-looks-controller', LooksController);
  }

  /* ============================================================
     Deep links: /pages/styled-for-you?piece=<handle>#look-<id>
     ============================================================ */
  function openLinkedLook() {
    var hash = window.location.hash || '';
    if (hash.indexOf('#look-') !== 0) return;
    var look = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!look || !look.classList.contains('sfy-look')) return;

    var controller = document.querySelector('sfy-looks-controller');
    if (controller && typeof controller.reveal === 'function') controller.reveal(look);

    document.querySelectorAll('.sfy-look.is-linked, .sfy-item.is-linked').forEach(function (el) {
      el.classList.remove('is-linked');
    });
    look.classList.add('is-linked');

    var piece = null;
    try { piece = new URLSearchParams(window.location.search).get('piece'); } catch (e) {}
    var host = look.closest('sfy-shop-the-look');
    if (piece && host && typeof host.highlightPiece === 'function') {
      var row = look.querySelector('.sfy-item[data-handle="' + piece.replace(/"/g, '') + '"]');
      host.highlightPiece(look, row);
    }

    window.requestAnimationFrame(function () {
      look.scrollIntoView({ block: 'start' });
    });
  }

  window.addEventListener('hashchange', openLinkedLook);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openLinkedLook);
  } else {
    openLinkedLook();
  }

  /* ============================================================
     Product pages: "Looks with this product"
     Reads the Styled for You page once, finds the looks that
     include this product, and links straight to them.
     ============================================================ */
  var CACHE_MINUTES = 10;

  function readLooksFrom(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.prototype.slice.call(doc.querySelectorAll('.sfy-look[data-look-id]')).map(function (look) {
      var img = look.querySelector('.sfy-look__photo img');
      return {
        id: look.dataset.lookId,
        products: (look.dataset.products || '').split(' ').filter(Boolean),
        occasion: look.dataset.occasion || '',
        title: look.dataset.title || '',
        count: parseInt(look.dataset.count || '0', 10),
        total: look.dataset.total || '',
        src: img ? img.getAttribute('src') : '',
        srcset: img ? (img.getAttribute('srcset') || '') : '',
        alt: img ? (img.getAttribute('alt') || '') : ''
      };
    });
  }

  function loadLooks(source) {
    var key = 'sfy-looks:' + source;
    try {
      var cached = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.at < CACHE_MINUTES * 60000 && !(window.Shopify && Shopify.designMode)) {
        return Promise.resolve(cached.looks);
      }
    } catch (e) {}

    return fetch(source, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Could not load ' + source);
        return res.text();
      })
      .then(function (html) {
        var looks = readLooksFrom(html);
        try { window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), looks: looks })); } catch (e) {}
        return looks;
      });
  }

  class ProductLooks extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      var self = this;

      if ('IntersectionObserver' in window && !(window.Shopify && Shopify.designMode)) {
        var io = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) {
            io.disconnect();
            self.load();
          }
        }, { rootMargin: '800px 0px' });
        /* the element is hidden until it has cards, so watch its section */
        io.observe(this.closest('.shopify-section') || this.parentNode);
      } else {
        this.load();
      }
    }

    load() {
      var self = this;
      var handle = this.dataset.handle;
      var max = parseInt(this.dataset.max || '3', 10);

      loadLooks(this.dataset.source)
        .then(function (looks) {
          var mine = looks.filter(function (look) { return look.products.indexOf(handle) !== -1; }).slice(0, max);
          self.render(mine);
        })
        .catch(function () {
          if (window.Shopify && Shopify.designMode) self.render([]);
        });
    }

    render(looks) {
      var grid = this.querySelector('[data-cards]');
      if (!grid) return;
      grid.innerHTML = '';

      if (!looks.length) {
        if (window.Shopify && Shopify.designMode) {
          var note = document.createElement('p');
          note.className = 'sfy-head__text sfy-editor-note';
          note.textContent = this.dataset.editorEmpty;
          grid.appendChild(note);
          this.hidden = false;
        }
        return;
      }

      var self = this;
      var source = this.dataset.source;
      var handle = this.dataset.handle;
      grid.dataset.count = looks.length;

      looks.forEach(function (look) {
        var a = document.createElement('a');
        a.className = 'sfy-card';
        a.href = source + '?piece=' + encodeURIComponent(handle) + '#look-' + encodeURIComponent(look.id);

        var photo = document.createElement('div');
        photo.className = 'sfy-card__photo';
        if (look.src) {
          var img = document.createElement('img');
          img.src = look.src;
          if (look.srcset) img.srcset = look.srcset;
          img.sizes = '(min-width: 990px) 33vw, (min-width: 768px) 50vw, 100vw';
          img.alt = look.alt;
          img.loading = 'lazy';
          photo.appendChild(img);
        }
        a.appendChild(photo);

        var body = document.createElement('div');
        body.className = 'sfy-card__body';
        if (look.occasion) {
          var occ = document.createElement('p');
          occ.className = 'sfy-card__occ';
          occ.textContent = look.occasion;
          body.appendChild(occ);
        }
        if (look.title) {
          var title = document.createElement('h3');
          title.className = 'sfy-card__title';
          title.textContent = look.title;
          body.appendChild(title);
        }
        if (self.dataset.showMeta === 'true' && look.count) {
          var meta = document.createElement('p');
          meta.className = 'sfy-card__count';
          meta.textContent = (self.dataset.meta || '[n] pieces')
            .replace('[n]', look.count)
            .replace('[amount]', look.total);
          body.appendChild(meta);
        }
        var cta = document.createElement('span');
        cta.className = 'sfy-card__cta';
        cta.innerHTML = '<span></span> <span aria-hidden="true">&rarr;</span>';
        cta.firstChild.textContent = self.dataset.cta || 'Shop the look';
        body.appendChild(cta);

        a.appendChild(body);
        grid.appendChild(a);
      });

      this.hidden = false;
    }
  }

  if (!customElements.get('sfy-product-looks')) {
    customElements.define('sfy-product-looks', ProductLooks);
  }
})();
